import { useRef, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DropzoneProps {
  /** Allow selecting/dropping more than one file at once. */
  multiple?: boolean;
  /** Passed straight through to the underlying file input's `accept`. */
  accept?: string;
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  className?: string;
}

/**
 * Reusable dashed-border drag-and-drop file picker. Generalizes the drag
 * handling first written for `UploadInventoryDialog` (single-file, dialog-only)
 * so other upload surfaces can drop in multi-file drag-and-drop without
 * re-implementing `onDragOver`/`onDrop` plumbing.
 */
export function Dropzone({
  multiple = false,
  accept,
  onFilesSelected,
  disabled = false,
  label = 'Drop files here or browse',
  helperText,
  className,
}: DropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const emitFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = multiple ? Array.from(list) : [list[0]];
    onFilesSelected(files);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    emitFiles(e.dataTransfer.files);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && fileInputRef.current?.click()}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-5 text-center transition-colors',
        isDragging
          ? 'border-primary-s500 bg-primary-s50'
          : 'border-primary-s500/40 bg-info-light',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <UploadCloud size={20} className="text-lightGray" />
      <p className="text-sm text-lightGray">
        {label.includes('browse') ? (
          <>
            {label.replace(/browse$/, '')}
            <span className="text-primary-s500 hover:underline">browse</span>
          </>
        ) : (
          label
        )}
      </p>
      {helperText && (
        <p className="text-xs font-medium text-lightGray">{helperText}</p>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          emitFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
